import type { TFunction } from 'i18next';

// WP1 part 2: an invoiced order stays editable, but its invoiced lines keep
// their reference (no removal, no unit change). Stable code → Polish copy.
// ORDER_PRODUCT_QUANTITY_PAIR_INCONSISTENT: the quantity in the base unit and
// in the price unit do not match the product's dimensions.
const SAVE_ERROR_KEYS: Record<string, string> = {
  ORDER_PRODUCT_INVOICED_LINE_REMOVAL_BLOCKED: 'orders:error.invoicedLineRemovalBlocked',
  ORDER_PRODUCT_INVOICED_UNIT_CHANGE_BLOCKED: 'orders:error.invoicedUnitChangeBlocked',
  ORDER_PRODUCT_QUANTITY_PAIR_INCONSISTENT: 'orders:error.quantityPairInconsistent',
};

/**
 * The manual number is already the number (manual or auto) of another order —
 * 400 ORDER_NUMBER_TAKEN. `orderId` comes only when the user may open that
 * order. The number modal shows it on the field (with a link), not as a toast.
 */
export interface OrderNumberTaken {
  number: string | null;
  orderId: string | null;
}

export function getOrderNumberTaken(error: unknown): OrderNumberTaken | null {
  const data = (error as { response?: { data?: { code?: unknown; number?: unknown; orderId?: unknown } } })?.response?.data;
  if (data?.code !== 'ORDER_NUMBER_TAKEN') return null;
  const number = typeof data.number === 'string' && data.number.trim() ? data.number.trim() : null;
  const orderId = typeof data.orderId === 'string' && data.orderId ? data.orderId : null;
  return { number, orderId };
}

/** Plain-text form (toasts of the create / draft save). */
export function getOrderNumberTakenMessage(taken: OrderNumberTaken, t: TFunction): string {
  return taken.number
    ? String(t('orders:error.orderNumberTaken', { number: taken.number }))
    : String(t('orders:error.orderNumberTakenUnnamed'));
}

export const getOrderProductSaveErrorMessage = (t: TFunction, error: unknown, fallback: string) => {
  // API `message` / `error` values are diagnostic prose (often English), not
  // a stable Polish UI contract. Known actionable codes map to their own
  // copy; all other save failures use the caller's precise Polish fallback.
  // The manual number is already another order's number (create / draft save).
  const numberTaken = getOrderNumberTaken(error);
  if (numberTaken) return getOrderNumberTakenMessage(numberTaken, t);
  const code = (error as { response?: { data?: { code?: unknown } } })?.response?.data?.code;
  if (typeof code === 'string' && SAVE_ERROR_KEYS[code]) {
    return String(t(SAVE_ERROR_KEYS[code]));
  }
  return fallback;
};

export interface OrderProductDeleteCargoConflictResponse {
  code?: unknown;
  routeNumbers?: unknown;
  productNames?: unknown;
}

const toStringList = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];

// S32 (order-product delete guard): PUT /orders/:id/products answers 400
// ORDER_PRODUCT_HAS_TRANSPORT_CARGO when the payload would delete a line that
// is still loaded on a transport (see orderProductsService
// .assertDeletableWithoutOrphaningCargo). The backend's `message` is an
// English sentence meant for logs/API consumers — map the stable `code` to
// Polish-first copy here instead of echoing it, same pattern as
// transportRouteViews.mapTransportMutationErrorToMessage. Returns undefined
// for any other error so callers fall through to
// getOrderProductSaveErrorMessage's generic message/fallback handling.
export const getOrderProductDeleteCargoConflictMessage = (
  t: TFunction,
  error: unknown,
): string | undefined => {
  const responseData = (error as { response?: { data?: OrderProductDeleteCargoConflictResponse } })
    ?.response?.data;

  if (responseData?.code !== 'ORDER_PRODUCT_HAS_TRANSPORT_CARGO') {
    return undefined;
  }

  return t('orders:orderDetail.messages.productHasTransportCargoError', {
    products: toStringList(responseData.productNames).join(', '),
    routes: toStringList(responseData.routeNumbers).join(', '),
  });
};
